/* ==========================================================================
   VicCraftCo Resin — theme scripts
   Vanilla JS, no dependencies. Custom elements are progressive: if JS fails,
   links and forms still work as plain HTML.
   ========================================================================== */

(function () {
  'use strict';

  const T = window.theme || {};

  /* --- helpers ---------------------------------------------------------- */

  function formatMoney(cents) {
    const format = T.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, withCommas + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }

  function debounce(fn, wait) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), wait);
    };
  }

  function trapFocus(container) {
    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return () => {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', handler);
    first.focus();
    return () => container.removeEventListener('keydown', handler);
  }

  /* --- hero slider ------------------------------------------------------- */

  class HeroSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.hero__slide'));
      if (this.slides.length === 0) return;

      this.dots = Array.from(this.querySelectorAll('.hero__dot'));
      this.index = 0;
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = parseInt(this.dataset.speed, 10) || 6000;
      this.timer = null;

      if (this.slides.length < 2) {
        this.querySelectorAll('.hero__arrow, .hero__dots').forEach((el) => el.remove());
        return;
      }

      this.querySelector('.hero__arrow--prev')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('.hero__arrow--next')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));

      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());

      this.bindSwipe();
      this.bindKeys();
      this.start();

      document.addEventListener('visibilitychange', () => {
        document.hidden ? this.stop() : this.start();
      });
    }

    disconnectedCallback() {
      this.stop();
    }

    go(next, userInitiated) {
      const total = this.slides.length;
      this.index = ((next % total) + total) % total;
      this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
      this.dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === this.index);
        d.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
      });
      if (userInitiated) this.restart();
    }

    start() {
      if (!this.autoplay || this.timer) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }

    bindSwipe() {
      let startX = null;
      let startY = null;
      this.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          this.go(this.index + (dx < 0 ? 1 : -1), true);
        }
        startX = null;
      }, { passive: true });
    }

    bindKeys() {
      this.setAttribute('tabindex', '-1');
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.go(this.index - 1, true);
        if (e.key === 'ArrowRight') this.go(this.index + 1, true);
      });
    }
  }
  customElements.define('hero-slider', HeroSlider);

  /* --- announcement rotator ---------------------------------------------- */

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.announcement__slide'));
      if (this.slides.length < 2) {
        this.querySelectorAll('.announcement__arrow').forEach((el) => el.remove());
        return;
      }
      this.index = 0;
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
      if (this.dataset.autoplay === 'true') {
        setInterval(() => this.go(this.index + 1), parseInt(this.dataset.speed, 10) || 5000);
      }
    }
    go(next) {
      const total = this.slides.length;
      this.index = ((next % total) + total) % total;
      this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* --- drawers ----------------------------------------------------------- */

  const DrawerManager = {
    releaseFocus: null,
    open(id) {
      const drawer = document.getElementById(id);
      if (!drawer) return;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      this.releaseFocus = trapFocus(drawer);
      this.active = drawer;
    },
    close(drawer) {
      const el = drawer || this.active;
      if (!el) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      if (this.releaseFocus) this.releaseFocus();
      this.active = null;
    }
  };
  window.DrawerManager = DrawerManager;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      DrawerManager.open(opener.dataset.drawerOpen);
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      DrawerManager.close(closer.closest('.drawer'));
    }
  });
  function syncProductThumbs() {
  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const main = gallery.querySelector('.product__media-main');
    const thumbs = gallery.querySelector('.product__thumbs');
    if (!main || !thumbs) return;
    if (window.innerWidth < 750) {
      thumbs.style.maxHeight = '';
      return;
    }
    thumbs.style.maxHeight = main.offsetHeight + 'px';
  });
}
window.addEventListener('load', syncProductThumbs);
window.addEventListener('resize', syncProductThumbs);
document.querySelectorAll('.product__media-main img').forEach((img) => {
  if (img.complete) return;
  img.addEventListener('load', syncProductThumbs, { once: true });
});

const productLightbox = document.querySelector('[data-product-lightbox]');
if (productLightbox) {
  const zoomImage = productLightbox.querySelector('[data-zoom-image]');
  const zoomCount = productLightbox.querySelector('[data-zoom-count]');
  let zoomSlides = [];
  let zoomIndex = 0;

  const renderZoom = () => {
    const slide = zoomSlides[zoomIndex];
    if (!slide) return;
    zoomImage.src = slide.src;
    zoomImage.alt = slide.alt;
    if (zoomCount) zoomCount.textContent = (zoomIndex + 1) + ' / ' + zoomSlides.length;
  };
  const openZoom = (start) => {
    zoomSlides = [...document.querySelectorAll('[data-zoom-open]')].map((el) => ({
      src: el.dataset.zoomSrc,
      alt: el.dataset.zoomAlt || ''
    }));
    if (!zoomSlides.length) return;
    zoomIndex = Math.min(Math.max(start, 0), zoomSlides.length - 1);
    renderZoom();
    productLightbox.hidden = false;
    document.documentElement.style.overflow = 'hidden';
  };
  const closeZoom = () => {
    productLightbox.hidden = true;
    document.documentElement.style.overflow = '';
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-zoom-open]');
    if (opener) {
      const all = [...document.querySelectorAll('[data-zoom-open]')];
      openZoom(all.indexOf(opener));
      return;
    }
    if (productLightbox.hidden) return;
    if (e.target.closest('[data-zoom-close]') || e.target === productLightbox) {
      closeZoom();
      return;
    }
    if (e.target.closest('[data-zoom-prev]')) {
      zoomIndex = (zoomIndex - 1 + zoomSlides.length) % zoomSlides.length;
      renderZoom();
    }
    if (e.target.closest('[data-zoom-next]')) {
      zoomIndex = (zoomIndex + 1) % zoomSlides.length;
      renderZoom();
    }
  });
  document.addEventListener('keydown', (e) => {
    if (productLightbox.hidden) return;
    if (e.key === 'Escape') closeZoom();
    if (e.key === 'ArrowLeft') {
      zoomIndex = (zoomIndex - 1 + zoomSlides.length) % zoomSlides.length;
      renderZoom();
    }
    if (e.key === 'ArrowRight') {
      zoomIndex = (zoomIndex + 1) % zoomSlides.length;
      renderZoom();
    }
  });
}

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (DrawerManager.active) DrawerManager.close();
    const search = document.querySelector('[data-search-modal]');
    if (search && !search.hidden) closeSearch();
  });

  /* --- search modal ------------------------------------------------------ */

  function openSearch() {
    const modal = document.querySelector('[data-search-modal]');
    if (!modal) return;
    modal.hidden = false;
    document.body.classList.add('overflow-hidden');
    modal.querySelector('[data-search-input]')?.focus();
  }

  function closeSearch() {
    const modal = document.querySelector('[data-search-modal]');
    if (!modal) return;
    modal.hidden = true;
    document.body.classList.remove('overflow-hidden');
    const results = modal.querySelector('[data-predictive-results]');
    if (results) results.innerHTML = '';
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-search-open]')) {
      e.preventDefault();
      openSearch();
    } else if (e.target.closest('[data-search-close]')) {
      e.preventDefault();
      closeSearch();
    }
  });

  const searchInput = document.querySelector('[data-search-input]');
  if (searchInput) {
    const results = document.querySelector('[data-predictive-results]');
    searchInput.addEventListener('input', debounce(function () {
      const q = this.value.trim();
      if (q.length < 2) {
        results.innerHTML = '';
        return;
      }
      fetch(`${T.routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`)
        .then((r) => (r.ok ? r.text() : Promise.reject(r)))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = doc.querySelector('[data-predictive-inner]');
          results.innerHTML = inner ? inner.innerHTML : '';
        })
        .catch(() => { results.innerHTML = ''; });
    }, 280));
  }

  /* --- sticky header ----------------------------------------------------- */

  const headerWrapper = document.querySelector('.header-wrapper');
  if (headerWrapper) {
    const onScroll = () => {
      headerWrapper.classList.toggle('is-scrolled', window.scrollY > 40);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* --- quantity inputs --------------------------------------------------- */

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.quantity__button');
    if (!btn) return;
    e.preventDefault();
    const wrapper = btn.closest('.quantity');
    const input = wrapper.querySelector('.quantity__input');
    const step = btn.dataset.action === 'increase' ? 1 : -1;
    const min = parseInt(input.min, 10) || 1;
    const next = Math.max(min, (parseInt(input.value, 10) || min) + step);
    if (next === parseInt(input.value, 10)) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* --- cart -------------------------------------------------------------- */

  const Cart = {
    async add(formData, button) {
      button?.classList.add('is-disabled');
      try {
        const res = await fetch(T.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: formData
        });
        const data = await res.json();
        if (data.status) throw new Error(data.description || data.message);
        await this.refresh();
        if (T.cartType === 'drawer') DrawerManager.open('CartDrawer');
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } catch (err) {
        this.showError(button, err.message);
      } finally {
        button?.classList.remove('is-disabled');
      }
    },

    async change(key, quantity) {
      const body = JSON.stringify({ id: key, quantity: quantity });
      const res = await fetch(T.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body
      });
      await res.json();
      await this.refresh();
      document.dispatchEvent(new CustomEvent('cart:updated'));
    },

    async refresh() {
      const res = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble,main-cart`);
      let sections = {};
      try {
        sections = await res.json();
      } catch (e) {
        window.location.reload();
        return;
      }
      this.replaceSection(sections['cart-drawer'], '#CartDrawer', '.drawer__inner-content');
      this.replaceSection(sections['cart-icon-bubble'], '[data-cart-icon-bubble]', '[data-cart-icon-inner]');
      this.replaceSection(sections['main-cart'], '#MainCartSection', '[data-cart-inner]');
    },

    replaceSection(html, targetSelector, innerSelector) {
      if (!html) return;
      const target = document.querySelector(targetSelector);
      if (!target) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = doc.querySelector(innerSelector);
      const dest = target.querySelector(innerSelector);
      if (source && dest) dest.innerHTML = source.innerHTML;
    },

    showError(button, message) {
      if (!button) return;
      const holder = button.closest('form')?.querySelector('[data-cart-error]');
      if (holder) {
        holder.textContent = message || 'Something went wrong.';
        holder.hidden = false;
      } else {
        console.error(message);
      }
    }
  };
  window.Cart = Cart;

  /* --- custom line-item options ------------------------------------------ */

  // The pickers live outside <form> and join it through form="ProductForm", so
  // they are not reachable from form.querySelector — scope to the section, and
  // validate here because the product form is novalidate.
  function validateCustomOptions(form) {
    const scope = form.closest('[data-product-section]') || document;
    const groups = scope.querySelectorAll('[data-custom-option][data-required="true"]');
    let firstInvalid = null;

    groups.forEach((group) => {
      const filled = Array.from(group.querySelectorAll('input, select')).some((el) =>
        el.type === 'radio' || el.type === 'checkbox' ? el.checked : el.value.trim() !== ''
      );
      group.classList.toggle('has-error', !filled);
      const message = group.querySelector('[data-custom-option-error]');
      if (message) message.hidden = filled;
      if (!filled && !firstInvalid) firstInvalid = group;
    });

    if (firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstInvalid.querySelector('input, select')?.focus({ preventScroll: true });
    }
    return !firstInvalid;
  }

  // Echo the choice next to the label, the way the variant picker does.
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-custom-option-input]');
    if (!input) return;
    const group = input.closest('[data-custom-option]');
    if (!group) return;
    const label = group.querySelector('[data-custom-option-selected]');
    if (label) label.textContent = input.value;
    group.classList.remove('has-error');
    const message = group.querySelector('[data-custom-option-error]');
    if (message) message.hidden = true;
  });

  // add-to-cart forms
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    if (!validateCustomOptions(form)) {
      e.preventDefault();
      return;
    }
    if (T.cartType !== 'drawer' && !form.hasAttribute('data-ajax')) return;
    e.preventDefault();
    const button = form.querySelector('[data-add-to-cart]');
    const errorHolder = form.querySelector('[data-cart-error]');
    if (errorHolder) errorHolder.hidden = true;
    Cart.add(new FormData(form), button);
  });

  // quantity change / remove inside cart
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-quantity]');
    if (!input) return;
    Cart.change(input.dataset.key, parseInt(input.value, 10) || 0);
  });

  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (!remove) return;
    e.preventDefault();
    Cart.change(remove.dataset.key, 0);
  });

  /* --- variant picker ---------------------------------------------------- */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-product-section]');
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.addEventListener('change', () => this.onChange());
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('input:checked, select')).map((el) => el.value);
    }

    onChange() {
      const selected = this.selectedOptions;
      const variant = this.variants.find((v) =>
        v.options.every((opt, i) => opt === selected[i])
      );
      this.updateLabels(selected);
      this.updateUrl(variant);
      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateMedia(variant);
      this.updateIdInput(variant);
      document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant } }));
    }

    updateLabels(selected) {
      this.querySelectorAll('[data-option-value]').forEach((el) => {
        const index = parseInt(el.dataset.optionIndex, 10);
        el.textContent = selected[index] || '';
      });
    }

    updateUrl(variant) {
      if (!variant || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
    }

    updateIdInput(variant) {
      const input = this.section?.querySelector('[data-variant-id]');
      if (input && variant) input.value = variant.id;
    }

    updatePrice(variant) {
      const holder = this.section?.querySelector('[data-price-block]');
      if (!holder) return;
      if (!variant) {
        holder.style.opacity = '0.4';
        return;
      }
      holder.style.opacity = '1';
      const current = holder.querySelector('[data-price-current]');
      const compare = holder.querySelector('[data-price-compare]');
      const saving = holder.querySelector('[data-price-saving]');
      if (current) current.textContent = formatMoney(variant.price);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (compare) {
        compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        compare.hidden = !onSale;
      }
      if (saving) {
        if (onSale) {
          const percent = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          saving.textContent = `-${percent}%`;
          saving.hidden = false;
        } else {
          saving.hidden = true;
        }
      }
      holder.classList.toggle('price--on-sale', !!onSale);
    }

    updateButton(variant) {
      const button = this.section?.querySelector('[data-add-to-cart]');
      if (!button) return;
      const label = button.querySelector('[data-add-to-cart-text]') || button;
      if (!variant) {
        button.disabled = true;
        label.textContent = T.strings.unavailable;
      } else if (!variant.available) {
        button.disabled = true;
        label.textContent = T.strings.soldOut;
      } else {
        button.disabled = false;
        label.textContent = T.strings.addToCart;
      }
    }

    updateMedia(variant) {
      if (!variant || !variant.featured_media) return;
      const gallery = this.section?.querySelector('[data-product-gallery]');
      if (!gallery) return;
      const target = gallery.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
      if (target) target.click();
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* --- product gallery --------------------------------------------------- */

  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-media-id]');
    if (!thumb) return;
    const gallery = thumb.closest('[data-product-gallery]');
    if (!gallery) return;
    const id = thumb.dataset.mediaId;
    gallery.querySelectorAll('[data-media-id]').forEach((t) => t.classList.toggle('is-active', t === thumb));
    gallery.querySelectorAll('.product__media-item').forEach((item) => {
      item.classList.toggle('is-active', item.dataset.mediaItem === id);
    });
  });

  /* --- carousels --------------------------------------------------------- */

  function enableDragScroll(track) {
  let active = false;
  let dragging = false;
  let startX = 0;
  let startLeft = 0;
  let pointerId = null;

  track.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'touch') return;
    if (e.button !== 0) return;
    active = true;
    dragging = false;
    pointerId = e.pointerId;
    startX = e.clientX;
    startLeft = track.scrollLeft;
  });
  track.addEventListener('pointermove', (e) => {
    if (!active) return;
    const dx = e.clientX - startX;
    if (!dragging && Math.abs(dx) <= 8) return;
    if (!dragging) {
      dragging = true;
      track.setPointerCapture(pointerId);
    }
    track.scrollLeft = startLeft - dx;
  });
  const end = () => { active = false; };
  track.addEventListener('pointerup', end);
  track.addEventListener('pointercancel', end);
  track.addEventListener('click', (e) => {
    if (!dragging) return;
    e.preventDefault();
    e.stopPropagation();
    dragging = false;
  }, true);
}

  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.carousel__track');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      if (!this.track) return;

      enableDragScroll(this.track);
      this.prev?.addEventListener('click', () => this.scrollBy(-1));
      this.next?.addEventListener('click', () => this.scrollBy(1));
      this.track.addEventListener('scroll', debounce(() => this.updateButtons(), 100), { passive: true });
      this.updateButtons();
      this.startAutoplay();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    startAutoplay() {
      const delay = Number(this.dataset.autoplay);
      if (!delay) return;
      const tick = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 4;
        if (this.track.scrollLeft >= max) this.track.scrollTo({ left: 0, behavior: 'smooth' });
        else this.scrollBy(1);
      };
      const play = () => {
        clearInterval(this.timer);
        this.timer = setInterval(tick, delay);
      };
      play();
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', play);
      this.addEventListener('pointerdown', () => clearInterval(this.timer));
      this.addEventListener('pointerup', play);
    }

    scrollBy(dir) {
      const item = this.track.querySelector('.carousel__item');
      const step = item ? item.offsetWidth + 24 : this.track.clientWidth * 0.8;
      this.track.scrollBy({ left: step * dir, behavior: 'smooth' });
    }

    updateButtons() {
      if (!this.prev || !this.next) return;
      const max = this.track.scrollWidth - this.track.clientWidth - 4;
      this.prev.disabled = this.track.scrollLeft <= 4;
      this.next.disabled = this.track.scrollLeft >= max;
    }
  }
  customElements.define('product-carousel', ProductCarousel);

  class VideoReel extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('[data-drag-track]');
      if (track) enableDragScroll(track);
    }
  }
  customElements.define('video-reel', VideoReel);

  /* --- collection filters (auto-submit) ---------------------------------- */

  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('change', debounce(() => this.apply(), 250));
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.apply();
      });
    }

    async apply() {
      const params = new URLSearchParams(new FormData(this.form)).toString();
      const url = `${window.location.pathname}?${params}`;
      const results = document.querySelector('[data-collection-results]');
      results?.classList.add('is-loading');
      try {
        const res = await fetch(url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newResults = doc.querySelector('[data-collection-results]');
        const newFilters = doc.querySelector('[data-collection-filters]');
        if (newResults && results) results.innerHTML = newResults.innerHTML;
        if (newFilters) this.innerHTML = newFilters.innerHTML;
        window.history.pushState({}, '', url);
        this.connectedCallback();
      } catch (e) {
        window.location.href = url;
      } finally {
        results?.classList.remove('is-loading');
      }
    }
  }
  customElements.define('collection-filters', CollectionFilters);

  document.addEventListener('change', (e) => {
    const sort = e.target.closest('[data-sort-by]');
    if (!sort) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sort.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  /* --- countdown --------------------------------------------------------- */

  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      const target = new Date(this.dataset.date).getTime();
      if (isNaN(target)) {
        this.hidden = true;
        return;
      }
      this.tick(target);
      this.interval = setInterval(() => this.tick(target), 1000);
    }
    disconnectedCallback() { clearInterval(this.interval); }
    tick(target) {
      let diff = Math.max(0, target - Date.now());
      const days = Math.floor(diff / 86400000); diff -= days * 86400000;
      const hours = Math.floor(diff / 3600000); diff -= hours * 3600000;
      const minutes = Math.floor(diff / 60000); diff -= minutes * 60000;
      const seconds = Math.floor(diff / 1000);
      const set = (name, val) => {
        const el = this.querySelector(`[data-unit="${name}"]`);
        if (el) el.textContent = String(val).padStart(2, '0');
      };
      set('days', days); set('hours', hours); set('minutes', minutes); set('seconds', seconds);
    }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* --- product tabs ------------------------------------------------------ */

  class ProductTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-panel]'));
      if (!this.tabs.length) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab.dataset.tab));
      });

      // Mobile headers act as accordion toggles rather than tabs.
      this.querySelectorAll('[data-tab-mobile]').forEach((summary) => {
        summary.addEventListener('click', () => {
          const panel = summary.closest('[data-panel]');
          panel.classList.toggle('is-open');
        });
      });

      this.select('0');
    }

    select(index) {
      this.tabs.forEach((tab) => {
        const active = tab.dataset.tab === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.panels.forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.panel === index);
      });
    }
  }
  customElements.define('product-tabs', ProductTabs);

  /* --- sticky add to cart ------------------------------------------------- */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.querySelector('form[data-product-form]');
      this.anchor = document.querySelector('[data-add-to-cart]');
      if (!this.form || !this.anchor) return;

      this.button = this.querySelector('[data-sticky-atc-submit]');
      // Forward to the real form so line-item properties are never dropped.
      this.button?.addEventListener('click', () => {
        this.anchor.click();
      });

      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.hidden = !show;
          document.body.classList.toggle('has-sticky-atc', show);
        },
        { threshold: 0 }
      );
      this.observer.observe(this.anchor);

      document.addEventListener('variant:changed', (e) => this.sync(e.detail.variant));
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      document.body.classList.remove('has-sticky-atc');
    }

    sync(variant) {
      if (!this.button) return;
      const price = this.querySelector('.price__current');
      if (variant) {
        this.button.disabled = !variant.available;
        this.button.textContent = variant.available ? T.strings.addToCart : T.strings.soldOut;
        if (price) price.textContent = formatMoney(variant.price);
      } else {
        this.button.disabled = true;
        this.button.textContent = T.strings.unavailable;
      }
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* --- recently viewed ---------------------------------------------------- */

  const RecentlyViewed = {
    key: 'viccraft:recently-viewed',

    read() {
      try {
        const raw = localStorage.getItem(this.key);
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list.filter((id) => typeof id === 'number') : [];
      } catch (e) {
        // Private browsing or a corrupted entry — behave as if there is no history.
        return [];
      }
    },

    remember(id) {
      if (!id) return;
      try {
        const list = this.read().filter((existing) => existing !== id);
        list.unshift(id);
        localStorage.setItem(this.key, JSON.stringify(list.slice(0, 20)));
      } catch (e) {
        /* storage unavailable — recently viewed simply stays empty */
      }
    },

    async render(container) {
      const currentId = T.product ? T.product.id : null;
      const ids = this.read().filter((id) => id !== currentId);
      if (!ids.length) return;

      const limit = parseInt(container.dataset.limit, 10) || 8;
      const query = ids.slice(0, limit).map((id) => `id:${id}`).join(' OR ');
      const url =
        `${T.routes.search_url}?q=${encodeURIComponent(query)}` +
        `&type=product&section_id=${container.dataset.sectionId}`;

      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const items = doc.querySelectorAll('[data-recently-viewed-track] .carousel__item');
        if (!items.length) return;

        const track = container.querySelector('[data-recently-viewed-track]');
        items.forEach((item) => track.appendChild(item));
        container.hidden = false;
      } catch (e) {
        /* leave the section hidden */
      }
    }
  };

  if (T.product && T.product.id) RecentlyViewed.remember(T.product.id);

  document.querySelectorAll('[data-recently-viewed]').forEach((el) => RecentlyViewed.render(el));

  /* --- pickup availability ------------------------------------------------ */

  document.addEventListener('variant:changed', (e) => {
    const holder = document.querySelector('[data-pickup-holder]');
    const variant = e.detail.variant;
    if (!holder) return;
    if (!variant) {
      holder.innerHTML = '';
      return;
    }
    const url = `${window.location.pathname}?variant=${variant.id}&section_id=pickup-availability`;
    fetch(url)
      .then((r) => (r.ok ? r.text() : Promise.reject(r)))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-pickup-inner]');
        if (fresh) holder.innerHTML = fresh.outerHTML;
      })
      .catch(() => {
        // Leave the previous wording rather than blanking a working block.
      });
  });

  /* --- gift wrapping ------------------------------------------------------ */

  document.addEventListener('change', async (e) => {
    const checkbox = e.target.closest('[data-gift-wrap]');
    if (!checkbox) return;
    checkbox.disabled = true;
    try {
      if (checkbox.checked) {
        const body = new FormData();
        body.append('id', checkbox.dataset.variantId);
        body.append('quantity', '1');
        await fetch(T.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: body
        });
        await Cart.refresh();
      } else if (checkbox.dataset.key) {
        await Cart.change(checkbox.dataset.key, 0);
      }
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (err) {
      // Put the checkbox back where it was so it never lies about the cart.
      checkbox.checked = !checkbox.checked;
    } finally {
      checkbox.disabled = false;
    }
  });

  /* --- store locator search ----------------------------------------------- */

  class StoreLocator extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-store-search]');
      if (!this.input) return;
      this.stores = Array.from(this.querySelectorAll('[data-store]'));
      this.empty = this.querySelector('[data-store-empty]');
      this.input.addEventListener('input', debounce(() => this.filter(), 150));
    }

    filter() {
      const term = this.input.value.trim().toLowerCase();
      let visible = 0;
      this.stores.forEach((store) => {
        const match = !term || store.dataset.search.includes(term);
        store.hidden = !match;
        if (match) visible += 1;
      });
      if (this.empty) this.empty.hidden = visible > 0;
    }
  }
  customElements.define('store-locator', StoreLocator);

  /* --- deferred video ----------------------------------------------------- */

  class DeferredVideo extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-video-play]')?.addEventListener('click', () => this.load());
    }

    load() {
      const id = this.dataset.videoUrl;
      const type = this.dataset.videoType;
      if (!id) return;
      const src =
        type === 'vimeo'
          ? `https://player.vimeo.com/video/${id}?autoplay=1`
          : `https://www.youtube.com/embed/${id}?autoplay=1&playsinline=1`;

      const iframe = document.createElement('iframe');
      iframe.setAttribute('src', src);
      iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('title', this.closest('.video')?.dataset.title || 'Video');
      iframe.className = 'video__iframe';
      this.replaceChildren(iframe);
    }
  }
  customElements.define('deferred-video', DeferredVideo);

  /* --- countdown banner --------------------------------------------------- */

  class CountdownBanner extends HTMLElement {
    connectedCallback() {
      const target = new Date(this.dataset.date).getTime();
      if (isNaN(target)) return;
      if (Date.now() >= target) {
        this.end();
        return;
      }
      this.interval = setInterval(() => {
        if (Date.now() >= target) this.end();
      }, 1000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    end() {
      clearInterval(this.interval);
      if (this.dataset.onEnd === 'hide') {
        this.hidden = true;
        return;
      }
      this.querySelector('countdown-timer')?.remove();
      this.querySelector('.countdown-banner__button')?.remove();
      const message = this.querySelector('[data-countdown-ended]');
      if (message) message.hidden = false;
    }
  }
  customElements.define('countdown-banner', CountdownBanner);

  /* --- back to top -------------------------------------------------------- */

  const scrollTopButton = document.querySelector('[data-scroll-top]');
  if (scrollTopButton) {
    const toggle = () => {
      scrollTopButton.hidden = window.scrollY < 600;
    };
    toggle();
    window.addEventListener('scroll', toggle, { passive: true });
    scrollTopButton.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /* --- localization selectors --------------------------------------------- */

  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-localization-select]');
    if (!select) return;
    const form = select.closest('form');
    const hidden = form?.querySelector(`input[name="${select.name}"]`);
    if (hidden) hidden.value = select.value;
    form?.submit();
  });

  /* --- share -------------------------------------------------------------- */

  document.addEventListener('click', (e) => {
    const button = e.target.closest('[data-share-copy]');
    if (!button) return;
    const url = button.dataset.shareCopy;
    const done = () => {
      const original = button.getAttribute('aria-label');
      button.setAttribute('aria-label', T.strings.linkCopied);
      button.classList.add('is-copied');
      setTimeout(() => {
        button.setAttribute('aria-label', original);
        button.classList.remove('is-copied');
      }, 2000);
    };
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(done).catch(() => {});
    }
  });

  /* --- scroll reveal ----------------------------------------------------- */

  if (document.body.classList.contains('animations-enabled') && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -60px 0px', threshold: 0.05 });

    const observeAll = () => document.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => observer.observe(el));
    observeAll();
    document.addEventListener('shopify:section:load', observeAll);

    /* Markup injected after load (product recommendations, filters, quick view)
       never passes through the calls above, so it would stay at opacity 0 —
       invisible but still clickable. Pick it up as it lands. */
    if ('MutationObserver' in window) {
      const rescan = debounce(observeAll, 100);
      new MutationObserver((records) => {
        for (const record of records) {
          for (const node of record.addedNodes) {
            if (node.nodeType !== 1) continue;
            if (node.classList.contains('reveal') || node.querySelector('.reveal')) {
              rescan();
              return;
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }


  /* ==========================================================================
     Resin sections
     ========================================================================== */

  /* --- carousel dots ------------------------------------------------------
     Extends the plain carousel with page indicators. One dot per "page" of
     visible items, so a 4-up carousel of 8 products shows two dots rather
     than eight. */

  class DealCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.carousel__track');
      this.dots = this.querySelector('[data-carousel-dots]');
      if (!this.track) return;

      this.build();
      this.track.addEventListener('scroll', debounce(() => this.sync(), 80), { passive: true });
      window.addEventListener('resize', debounce(() => this.build(), 200));
    }

    pageCount() {
      const width = this.track.clientWidth;
      if (!width) return 1;
      return Math.max(1, Math.ceil(this.track.scrollWidth / width));
    }

    build() {
      if (!this.dots) return;
      const pages = this.pageCount();

      if (pages < 2) {
        this.dots.hidden = true;
        this.dots.innerHTML = '';
        return;
      }

      this.dots.hidden = false;
      this.dots.innerHTML = '';
      for (let i = 0; i < pages; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'carousel__dot';
        dot.setAttribute('aria-label', (i + 1) + ' / ' + pages);
        dot.addEventListener('click', () => {
          this.track.scrollTo({ left: this.track.clientWidth * i, behavior: 'smooth' });
        });
        this.dots.appendChild(dot);
      }
      this.sync();
    }

    sync() {
      if (!this.dots || this.dots.hidden) return;
      const width = this.track.clientWidth || 1;
      const active = Math.round(this.track.scrollLeft / width);
      Array.from(this.dots.children).forEach((dot, i) => {
        dot.classList.toggle('is-active', i === active);
      });
    }
  }
  customElements.define('deal-carousel', DealCarousel);

  /* --- newsletter popup ---------------------------------------------------
     Opens once after a delay and remembers the dismissal, so it never nags on
     every page view. In the theme editor it opens immediately and skips
     storage, otherwise the merchant could never see what they are editing. */

  class NewsletterPopup extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.popup__panel');
      this.inEditor = Boolean(window.Shopify && window.Shopify.designMode);

      this.querySelectorAll('[data-popup-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      this.onKeydown = (e) => {
        if (e.key === 'Escape' && !this.hidden) this.close();
      };
      document.addEventListener('keydown', this.onKeydown);

      /* A successful signup re-renders the page with the form already in its
         success state, so show that straight away rather than after the delay. */
      if (this.querySelector('.form-status--success')) {
        this.open();
        return;
      }

      if (this.inEditor) {
        this.open();
        return;
      }

      if (this.dismissed()) return;
      const delay = parseInt(this.dataset.delay, 10) || 0;
      this.timer = setTimeout(() => this.open(), delay);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('keydown', this.onKeydown);
      document.body.classList.remove('overflow-hidden');
    }

    store() {
      return this.dataset.frequency === 'session' ? window.sessionStorage : window.localStorage;
    }

    dismissed() {
      try {
        const raw = this.store().getItem(this.dataset.storageKey);
        if (!raw) return false;
        if (this.dataset.frequency !== 'week') return true;
        return Date.now() - Number(raw) < 7 * 86400000;
      } catch (e) {
        /* Private browsing blocks storage. Showing the popup once per page
           beats throwing on the way in. */
        return false;
      }
    }

    open() {
      this.hidden = false;
      document.body.classList.add('overflow-hidden');
      this.releaseFocus = trapFocus(this.panel || this);
    }

    close() {
      clearTimeout(this.timer);
      this.hidden = true;
      document.body.classList.remove('overflow-hidden');
      if (this.releaseFocus) this.releaseFocus();
      if (this.inEditor) return;
      try {
        this.store().setItem(this.dataset.storageKey, String(Date.now()));
      } catch (e) {
        /* Nothing to do — the popup just reappears next visit. */
      }
    }
  }
  customElements.define('newsletter-popup', NewsletterPopup);

  /* --- bundle (frequently bought together) --------------------------------
     Keeps the running total in sync with the checked boxes and adds every
     checked line in one request. */

  class BundleForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-bundle-form]');
      this.items = Array.from(this.querySelectorAll('[data-bundle-item]'));
      this.totalEl = this.querySelector('[data-bundle-total]');
      this.submit = this.querySelector('[data-bundle-submit]');
      this.submitText = this.querySelector('[data-bundle-submit-text]');
      this.errorEl = this.querySelector('[data-bundle-error]');
      this.discount = parseFloat(this.dataset.discount) || 0;
      if (!this.form) return;

      this.items.forEach((box) => {
        box.addEventListener('change', () => {
          const line = box.closest('.bundle__item');
          if (line) line.classList.toggle('is-off', !box.checked);
          this.updateTotal();
        });
      });

      /* The variant picker rewrites which variant is selected, so the first
         line — the product being viewed — has to follow it. */
      document.addEventListener('variant:changed', (e) => {
        if (!e.detail || !e.detail.variant) return;
        const idField = this.querySelector('[data-bundle-current-id]');
        if (idField) idField.value = e.detail.variant.id;
        if (this.items[0]) this.items[0].dataset.price = e.detail.variant.price;
        this.updateTotal();
      });

      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.updateTotal();
    }

    updateTotal() {
      const cents = this.items.reduce((sum, box) => {
        if (!box.checked) return sum;
        return sum + (parseInt(box.dataset.price, 10) || 0);
      }, 0);
      const final = this.discount > 0 ? Math.round(cents * (1 - this.discount / 100)) : cents;
      if (this.totalEl) this.totalEl.textContent = formatMoney(final);
      if (this.submit) this.submit.disabled = cents === 0;
    }

    async onSubmit(e) {
      e.preventDefault();
      if (this.errorEl) this.errorEl.hidden = true;

      const lines = this.items
        .filter((box) => box.checked)
        .map((box) => {
          const line = box.closest('.bundle__item');
          if (!line) return null;
          const idField = line.querySelector('[data-bundle-id]') || line.querySelector('[data-bundle-current-id]');
          return idField && idField.value ? { id: Number(idField.value), quantity: 1 } : null;
        })
        .filter(Boolean);

      if (!lines.length) return;

      this.submit.disabled = true;
      const original = this.submitText ? this.submitText.textContent : '';

      try {
        const res = await fetch(T.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: lines })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Cart error');

        if (this.submitText) this.submitText.textContent = T.strings.bundleAdded || 'Added!';
        await Cart.refresh();
        if (T.cartType === 'drawer') DrawerManager.open('CartDrawer');
        document.dispatchEvent(new CustomEvent('cart:updated'));
        setTimeout(() => {
          if (this.submitText) this.submitText.textContent = original;
        }, 2000);
      } catch (err) {
        if (this.errorEl) {
          this.errorEl.textContent = err.message || (T.strings.bundleError || 'Something went wrong.');
          this.errorEl.hidden = false;
        }
      } finally {
        this.submit.disabled = false;
      }
    }
  }
  customElements.define('bundle-form', BundleForm);

  /* --- theme editor support ---------------------------------------------- */

  document.addEventListener('shopify:section:load', (e) => {
    const drawer = e.target.querySelector('.drawer.is-open');
    if (drawer) document.body.classList.add('overflow-hidden');
  });

  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest('.hero__slide');
    if (slide) {
      const slider = slide.closest('hero-slider');
      const index = Array.from(slider.querySelectorAll('.hero__slide')).indexOf(slide);
      slider.stop();
      slider.go(index);
    }
  });

  document.addEventListener('shopify:block:deselect', (e) => {
    const slider = e.target.closest('hero-slider');
    if (slider) slider.start();
  });
})();
